import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@material-ui/core";
import { propuestasApi } from "src/services/propuestasApi";
import { BRAND } from "src/theme/brandTokens";
import { BotonPrimario, BotonSecundario } from "./_botones";

const CODIGO_LENGTH = 6;
const CODIGO_PATTERN = /^\d{6}$/;

// ─── Componente principal ───────────────────────────────────────────────────

/**
 * Dialog que orquesta el flujo de firma del portal público V2:
 *   1. Paso "generar" — captura el nombre de quien firma + envía OTP al correo
 *      del solicitante vía `generarOTP`.
 *   2. Paso "ingresar" — el cliente tipea el código de 6 dígitos recibido por
 *      correo. Al confirmar encadena `verificarOTP` -> `responderPropuestaV2`.
 *   3. Paso "enviando" — loader mientras `responderPropuestaV2` persiste en BD
 *      (la activación automática + PDF firmado + correos ocurren server-side
 *      fire-and-forget).
 *
 * El `codigo` se preserva entre reintentos dentro de la misma sesión — el
 * usuario puede corregir un typo sin tipear todo otra vez. Si el OTP expiró
 * o se consumió, ofrece link "Reenviar código" que retrocede a paso "generar".
 */
const OTPDialog = ({
  open,
  onClose,
  token,
  correoSolicitante,
  nombreSolicitante,
  respuestas,
  onSuccess,
}) => {
  const [paso, setPaso] = useState("generar"); // "generar" | "ingresar" | "enviando"
  const [codigo, setCodigo] = useState("");
  const [nombreAceptante, setNombreAceptante] = useState(
    nombreSolicitante || "",
  );
  const [error, setError] = useState(null);

  const [generarOTP, { isLoading: generando }] =
    propuestasApi.useGenerarOTPMutation();
  const [verificarOTP, { isLoading: verificando }] =
    propuestasApi.useVerificarOTPMutation();
  const [responder, { isLoading: enviando }] =
    propuestasApi.useResponderPropuestaV2Mutation();

  const busy = generando || verificando || enviando;

  const resetearEstado = () => {
    setPaso("generar");
    setCodigo("");
    setError(null);
    setNombreAceptante(nombreSolicitante || "");
  };

  const handleClose = () => {
    if (enviando) return; // bloqueo duro durante persistencia
    resetearEstado();
    onClose();
  };

  const handleGenerar = async () => {
    setError(null);
    try {
      await generarOTP({ token }).unwrap();
      setPaso("ingresar");
      setCodigo("");
    } catch (err) {
      setError(
        err?.data?.message ||
          "No pudimos enviar el código. Revisa el correo y reintenta.",
      );
    }
  };

  const handleReenviar = async () => {
    // UX recovery: el cliente puede pedir un OTP nuevo sin cerrar el dialog
    // si no recibió el anterior o expiró.
    setError(null);
    try {
      await generarOTP({ token }).unwrap();
      setCodigo("");
    } catch (err) {
      setError(
        err?.data?.message ||
          "No pudimos reenviar el código. Reintenta más tarde.",
      );
    }
  };

  const handleVerificarYResponder = async () => {
    setError(null);
    try {
      await verificarOTP({ token, otp: codigo }).unwrap();
    } catch (err) {
      setError(
        err?.data?.message ||
          "Código incorrecto o expirado. Reingresa o solicita uno nuevo.",
      );
      return;
    }
    setPaso("enviando");
    try {
      await responder({
        token,
        respuestas,
        datosAceptante: nombreAceptante.trim(),
        nombreCliente: nombreSolicitante || "",
      }).unwrap();
      onSuccess();
      resetearEstado();
    } catch (err) {
      setError(
        err?.data?.message ||
          "No pudimos registrar tu respuesta. Intenta nuevamente.",
      );
      setPaso("ingresar");
    }
  };

  const codigoValido = CODIGO_PATTERN.test(codigo.trim());
  const nombreValido = nombreAceptante.trim().length >= 2;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={enviando}
      PaperProps={{
        sx: {
          fontFamily: BRAND.font.family.base,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          pr: "84px",
          py: 3,
          color: BRAND.color.inkSoft,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 64,
            height: 64,
            bgcolor: BRAND.color.primary,
          }}
        />
        <Box
          sx={{
            fontSize: BRAND.font.size.eyebrow,
            color: BRAND.color.primary,
            textTransform: "uppercase",
            letterSpacing: BRAND.font.letterSpacing.widest,
            fontWeight: BRAND.font.weight.medium,
            mb: 0.5,
          }}
        >
          Firmar aceptación
        </Box>
        <Box
          sx={{
            fontFamily: BRAND.font.family.display,
            fontSize: BRAND.font.size.h3,
            fontWeight: BRAND.font.weight.black,
            color: BRAND.color.inkSoft,
            lineHeight: BRAND.font.lineHeight.snug,
            letterSpacing: BRAND.font.letterSpacing.tight,
          }}
        >
          Confirmar respuesta
        </Box>
      </DialogTitle>

      <DialogContent>
        {paso === "generar" && (
          <Stack spacing={2.5}>
            <Box
              sx={{
                fontSize: BRAND.font.size.body,
                color: BRAND.color.inkSoft,
                lineHeight: BRAND.font.lineHeight.body,
              }}
            >
              Para confirmar tu respuesta te enviaremos un código de
              verificación al correo{" "}
              <Box
                component="strong"
                sx={{ fontWeight: BRAND.font.weight.bold }}
              >
                {correoSolicitante || "registrado"}
              </Box>
              . El código tiene una validez de 5 minutos.
            </Box>
            <TextField
              fullWidth
              label="Tu nombre (quedará registrado como firmante)"
              value={nombreAceptante}
              onChange={(e) => setNombreAceptante(e.target.value)}
              variant="outlined"
              size="small"
              autoFocus
              disabled={generando}
            />
          </Stack>
        )}

        {paso === "ingresar" && (
          <Stack spacing={2.5}>
            <Box
              sx={{
                fontSize: BRAND.font.size.body,
                color: BRAND.color.inkSoft,
                lineHeight: BRAND.font.lineHeight.body,
              }}
            >
              Revisa tu correo{" "}
              <Box
                component="strong"
                sx={{ fontWeight: BRAND.font.weight.bold }}
              >
                {correoSolicitante}
              </Box>{" "}
              e ingresa el código de {CODIGO_LENGTH} dígitos que enviamos.
            </Box>
            <TextField
              autoFocus
              fullWidth
              label={`Código de ${CODIGO_LENGTH} dígitos`}
              value={codigo}
              onChange={(e) =>
                setCodigo(
                  e.target.value.replace(/\D/g, "").slice(0, CODIGO_LENGTH),
                )
              }
              variant="outlined"
              size="small"
              inputProps={{
                maxLength: CODIGO_LENGTH,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              disabled={verificando || enviando}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: 0.75,
                fontSize: BRAND.font.size.bodySmall,
              }}
            >
              <Box component="span" sx={{ color: BRAND.color.grey70 }}>
                ¿No recibiste el código?
              </Box>
              <Box
                component="button"
                type="button"
                onClick={handleReenviar}
                disabled={busy}
                sx={{
                  appearance: "none",
                  border: "none",
                  background: "none",
                  padding: 0,
                  font: "inherit",
                  fontSize: BRAND.font.size.bodySmall,
                  color: BRAND.color.primary,
                  textDecoration: "underline",
                  cursor: busy ? "default" : "pointer",
                  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                  "&:hover:not(:disabled)": { color: BRAND.color.primaryDark },
                }}
              >
                {generando ? "Reenviando…" : "Reenviar código"}
              </Box>
            </Box>
          </Stack>
        )}

        {paso === "enviando" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              py: 4,
            }}
          >
            <CircularProgress sx={{ color: BRAND.color.primary }} />
            <Box
              sx={{
                fontSize: BRAND.font.size.body,
                color: BRAND.color.inkSoft,
              }}
            >
              Registrando tu respuesta…
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <BotonSecundario onClick={handleClose} disabled={enviando}>
          Cancelar
        </BotonSecundario>
        {paso === "generar" && (
          <BotonPrimario
            onClick={handleGenerar}
            disabled={generando || !nombreValido}
            loading={generando}
          >
            Enviar código
          </BotonPrimario>
        )}
        {paso === "ingresar" && (
          <BotonPrimario
            onClick={handleVerificarYResponder}
            disabled={verificando || enviando || !codigoValido}
            loading={verificando || enviando}
          >
            Confirmar
          </BotonPrimario>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── PropTypes ──────────────────────────────────────────────────────────────

OTPDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  correoSolicitante: PropTypes.string,
  nombreSolicitante: PropTypes.string,
  respuestas: PropTypes.arrayOf(
    PropTypes.shape({
      item_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      decision: PropTypes.oneOf(["ACEPTADO", "RECHAZADO"]).isRequired,
      comentario_cliente: PropTypes.string,
    }),
  ).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

OTPDialog.defaultProps = {
  correoSolicitante: "",
  nombreSolicitante: "",
};

export default OTPDialog;
